import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  TrashIcon,
  PencilSquareIcon,
  CheckIcon,
  XMarkIcon,
  FolderIcon,
  ExclamationCircleIcon,
  LightBulbIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import type { Agrupamento, AgrupamentoSugestaoDto } from '../../api/agrupamento';

type FiltroEstado = 'todos' | 'vazios' | 'sugestoes' | 'completos';

interface Props {
  agrupamentos: Agrupamento[];
  agrupamentoSelecionado: Agrupamento | null;
  sugestoesMap: Record<number, AgrupamentoSugestaoDto>;
  carregando: boolean;
  termoBusca: string;
  onTermoBuscaChange: (termo: string) => void;
  onSelecionarAgrupamento: (agrupamento: Agrupamento) => void;
  onAbrirSugestao: (agrupamento: Agrupamento) => void;
  onCriarAgrupamento: () => void;
  onAbrirManutencao: () => void;
  onExcluirAgrupamento: (agrupamento: Agrupamento) => void;
  onRenomearAgrupamento: (agrupamento: Agrupamento, novaDescricao: string) => Promise<boolean>;
  onRecarregar: () => void;
  onRecalcularSugestoes: () => Promise<void>;
}

export const ListaAgrupamentos: React.FC<Props> = ({
  agrupamentos,
  agrupamentoSelecionado,
  sugestoesMap,
  carregando,
  termoBusca,
  onTermoBuscaChange,
  onSelecionarAgrupamento,
  onAbrirSugestao,
  onCriarAgrupamento,
  onAbrirManutencao,
  onExcluirAgrupamento,
  onRenomearAgrupamento,
  onRecarregar,
  onRecalcularSugestoes,
}) => {
  const [recalculando, setRecalculando] = useState(false);
  const [filtro, setFiltro] = useState<FiltroEstado>('todos');

  // Edição inline: id do grupo em edição + texto rascunho + flag de loading.
  // Reaproveita o padrão de hover/focus do botão de excluir — lápis só aparece
  // no hover/foco do item OU no item selecionado.
  const [editandoId, setEditandoId] = useState<number | null>(null);
  const [rascunho, setRascunho] = useState('');
  const [salvandoEdicao, setSalvandoEdicao] = useState(false);
  const editInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editandoId !== null) {
      editInputRef.current?.focus();
      editInputRef.current?.select();
    }
  }, [editandoId]);

  const iniciarEdicao = (e: React.MouseEvent, agrupamento: Agrupamento) => {
    e.stopPropagation();
    setEditandoId(agrupamento.grupoId);
    setRascunho(agrupamento.descricao);
  };

  const cancelarEdicao = () => {
    setEditandoId(null);
    setRascunho('');
  };

  const salvarEdicao = async (agrupamento: Agrupamento) => {
    const trimmed = rascunho.trim();
    if (!trimmed || trimmed === agrupamento.descricao) {
      cancelarEdicao();
      return;
    }
    setSalvandoEdicao(true);
    try {
      const ok = await onRenomearAgrupamento(agrupamento, trimmed);
      if (ok) cancelarEdicao();
    } finally {
      setSalvandoEdicao(false);
    }
  };

  const handleRecalcular = async () => {
    setRecalculando(true);
    try { await onRecalcularSugestoes(); } finally { setRecalculando(false); }
  };

  // Estatísticas
  const stats = useMemo(() => {
    const total = agrupamentos.length;
    const vazios = agrupamentos.filter(a => (a.qtdeProdutos ?? 0) === 0).length;
    const comSugestao = agrupamentos.filter(a => !!sugestoesMap[a.grupoId]).length;
    const completos = total - vazios;
    return { total, vazios, comSugestao, completos };
  }, [agrupamentos, sugestoesMap]);

  const progressoPct = stats.total > 0 ? Math.round((stats.completos / stats.total) * 100) : 0;

  // Filtra por estado
  const agrupamentosFiltrados = useMemo(() => {
    return agrupamentos.filter(a => {
      const vazio = (a.qtdeProdutos ?? 0) === 0;
      if (filtro === 'vazios') return vazio;
      if (filtro === 'sugestoes') return !vazio && !!sugestoesMap[a.grupoId];
      if (filtro === 'completos') return !vazio && !sugestoesMap[a.grupoId];
      return true;
    });
  }, [agrupamentos, sugestoesMap, filtro]);

  // Vazios primeiro, depois alfabético
  const agrupamentosOrdenados = useMemo(() => {
    return [...agrupamentosFiltrados].sort((a, b) => {
      const aVazio = (a.qtdeProdutos ?? 0) === 0;
      const bVazio = (b.qtdeProdutos ?? 0) === 0;
      if (aVazio !== bVazio) return aVazio ? -1 : 1;
      return (a.descricao ?? '').localeCompare(b.descricao ?? '', 'pt-BR');
    });
  }, [agrupamentosFiltrados]);

  const filtroLabel: Record<FiltroEstado, string> = {
    todos: `Todos (${stats.total})`,
    vazios: `Vazios (${stats.vazios})`,
    sugestoes: `Sugestões (${stats.comSugestao})`,
    completos: `OK (${stats.completos - stats.comSugestao})`,
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex-none p-3 border-b border-zinc-200 dark:border-zinc-700 space-y-3">

        {/* Título + ações */}
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">Grupos</h2>
          <div className="flex items-center gap-1">
            <button
              onClick={handleRecalcular}
              disabled={recalculando}
              className="p-1.5 text-amber-500 hover:text-amber-600 disabled:opacity-50 transition-colors rounded-md hover:bg-amber-50 dark:hover:bg-amber-900/20"
              title="Recalcular sugestões"
            >
              <LightBulbIcon className={`w-4 h-4 ${recalculando ? 'animate-pulse' : ''}`} />
            </button>
            <button
              onClick={onRecarregar}
              disabled={carregando}
              className="p-1.5 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 disabled:opacity-50 transition-colors rounded-md hover:bg-zinc-100 dark:hover:bg-zinc-700"
              title="Recarregar grupos"
            >
              <ArrowPathIcon className={`w-4 h-4 ${carregando ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={onAbrirManutencao}
              className="flex items-center gap-1 px-2 py-1.5 text-zinc-600 dark:text-zinc-300 border border-zinc-300 dark:border-zinc-600 hover:bg-zinc-100 dark:hover:bg-zinc-700 rounded-md transition-colors text-xs font-medium"
              title="Manutenção em lote"
            >
              <WrenchScrewdriverIcon className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Lote</span>
            </button>
            <button
              onClick={onCriarAgrupamento}
              className="flex items-center gap-1 px-2 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors text-xs font-medium"
            >
              <PlusIcon className="w-3.5 h-3.5" />
              Novo
            </button>
          </div>
        </div>

        {/* Barra de progresso */}
        {stats.total > 0 && (
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] text-zinc-500 dark:text-zinc-400">
                {stats.completos} de {stats.total} grupos com produtos
              </span>
              <span className={`text-[10px] font-semibold ${
                progressoPct === 100 ? 'text-emerald-600' :
                progressoPct >= 80 ? 'text-blue-600' : 'text-amber-600'
              }`}>
                {progressoPct}%
              </span>
            </div>
            <div className="h-1.5 rounded-full bg-zinc-200 dark:bg-zinc-700 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${
                  progressoPct === 100 ? 'bg-emerald-500' :
                  progressoPct >= 80 ? 'bg-blue-500' : 'bg-amber-500'
                }`}
                style={{ width: `${progressoPct}%` }}
              />
            </div>
            {stats.vazios > 0 && (
              <p className="text-[10px] text-rose-500 dark:text-rose-400 mt-1 flex items-center gap-1">
                <ExclamationCircleIcon className="w-3 h-3" />
                {stats.vazios} {stats.vazios === 1 ? 'grupo vazio bloqueia' : 'grupos vazios bloqueiam'} o catálogo
              </p>
            )}
          </div>
        )}

        {/* Filtros de estado */}
        <div className="flex gap-1 flex-wrap">
          {(['todos', 'vazios', 'sugestoes', 'completos'] as FiltroEstado[]).map(f => (
            <button
              key={f}
              onClick={() => setFiltro(f)}
              className={`px-2 py-0.5 rounded-full text-[10px] font-medium transition-colors border ${
                filtro === f
                  ? f === 'vazios'
                    ? 'bg-rose-500 text-white border-rose-500'
                    : f === 'sugestoes'
                      ? 'bg-amber-500 text-white border-amber-500'
                      : f === 'completos'
                        ? 'bg-emerald-600 text-white border-emerald-600'
                        : 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400 border-zinc-200 dark:border-zinc-700 hover:border-zinc-300'
              }`}
            >
              {filtroLabel[f]}
            </button>
          ))}
        </div>

        {/* Busca */}
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-400 pointer-events-none" />
          <input
            type="text"
            value={termoBusca}
            onChange={e => onTermoBuscaChange(e.target.value)}
            placeholder="Filtrar grupos..."
            className="w-full pl-8 pr-3 py-1.5 text-xs border border-zinc-300 dark:border-zinc-600 rounded-md
                       focus:ring-2 focus:ring-blue-500 focus:border-transparent
                       bg-white dark:bg-zinc-700 text-zinc-900 dark:text-zinc-100
                       placeholder-zinc-400 dark:placeholder-zinc-500"
          />
        </div>
      </div>

      {/* Lista */}
      <div className="flex-1 overflow-y-auto">
        {carregando ? (
          <div className="flex flex-col items-center justify-center py-10 text-zinc-400 dark:text-zinc-500">
            <ArrowPathIcon className="w-5 h-5 animate-spin mb-2" />
            <span className="text-xs">Carregando...</span>
          </div>
        ) : agrupamentosOrdenados.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-zinc-400 dark:text-zinc-500">
            <CheckCircleIcon className="w-8 h-8 mb-2 text-emerald-400" />
            <span className="text-xs text-center px-4">
              {filtro !== 'todos'
                ? `Nenhum grupo nesta categoria${termoBusca ? ' com esse filtro' : ''}`
                : termoBusca ? 'Nenhum grupo encontrado' : 'Nenhum grupo cadastrado'}
            </span>
          </div>
        ) : (
          agrupamentosOrdenados.map(agrupamento => {
            const vazio = (agrupamento.qtdeProdutos ?? 0) === 0;
            const selecionado = agrupamentoSelecionado?.grupoId === agrupamento.grupoId;
            const sugestao = sugestoesMap[agrupamento.grupoId];
            const emEdicao = editandoId === agrupamento.grupoId;

            return (
              <div
                key={agrupamento.grupoId}
                onClick={() => { if (!emEdicao) onSelecionarAgrupamento(agrupamento); }}
                className={`group relative flex items-center justify-between px-3 py-3 md:py-2.5 ${emEdicao ? '' : 'cursor-pointer'} transition-colors border-b
                  ${vazio
                    ? 'border-l-2 border-l-rose-500 border-b-rose-100 dark:border-b-rose-900/30'
                    : 'border-b-zinc-100 dark:border-b-zinc-700/60'
                  }
                  ${selecionado
                    ? vazio ? 'bg-rose-50 dark:bg-rose-900/20' : 'bg-blue-50 dark:bg-blue-900/20'
                    : vazio
                      ? 'bg-rose-50/50 dark:bg-rose-900/10 hover:bg-rose-100/60 dark:hover:bg-rose-900/20'
                      : 'hover:bg-zinc-50 dark:hover:bg-zinc-700/40'
                  }`}
              >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  {vazio
                    ? <ExclamationCircleIcon className="w-4 h-4 text-rose-500 flex-shrink-0" />
                    : <FolderIcon className="w-4 h-4 text-zinc-400 dark:text-zinc-500 flex-shrink-0" />
                  }
                  <div className="min-w-0 flex-1">
                    {emEdicao ? (
                      <input
                        ref={editInputRef}
                        type="text"
                        value={rascunho}
                        onClick={e => e.stopPropagation()}
                        onChange={e => setRascunho(e.target.value.toUpperCase())}
                        onKeyDown={e => {
                          if (e.key === 'Enter') salvarEdicao(agrupamento);
                          if (e.key === 'Escape') cancelarEdicao();
                        }}
                        disabled={salvandoEdicao}
                        maxLength={100}
                        className="w-full text-sm md:text-xs font-medium bg-white dark:bg-zinc-700 text-zinc-900 dark:text-zinc-100 border border-blue-400 dark:border-blue-500 rounded px-1.5 py-0.5 outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 uppercase"
                      />
                    ) : (
                      <p className={`text-sm md:text-xs font-medium truncate ${vazio ? 'text-rose-700 dark:text-rose-400' : 'text-zinc-900 dark:text-zinc-100'}`}>
                        {agrupamento.descricao}
                      </p>
                    )}
                    <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                      <span className={`text-[10px] ${vazio ? 'text-rose-500' : 'text-zinc-400 dark:text-zinc-500'}`}>
                        {vazio ? 'Grupo vazio — adicione produtos' : `${agrupamento.qtdeProdutos} produto${agrupamento.qtdeProdutos !== 1 ? 's' : ''}`}
                      </span>

                      {/* Badge clicável de sugestões */}
                      {sugestao && !vazio && !emEdicao && (
                        <button
                          onClick={e => {
                            e.stopPropagation();
                            onAbrirSugestao(agrupamento);
                          }}
                          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium
                                     bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400
                                     hover:bg-amber-200 dark:hover:bg-amber-900/60 transition-colors"
                          title={`Clique para ver ${sugestao.qtdeSugeridos} produto(s) sugerido(s) — busca: "${sugestao.termos}"`}
                        >
                          <LightBulbIcon className="w-2.5 h-2.5" />
                          {sugestao.qtdeSugeridos} sugerido{sugestao.qtdeSugeridos !== 1 ? 's' : ''}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                {emEdicao ? (
                  <div className="flex items-center gap-0.5 flex-shrink-0 ml-1">
                    <button
                      onClick={e => { e.stopPropagation(); salvarEdicao(agrupamento); }}
                      disabled={salvandoEdicao || !rascunho.trim()}
                      className="p-1.5 md:p-1 text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300 disabled:opacity-40 transition-colors rounded"
                      title="Salvar (Enter)"
                    >
                      <CheckIcon className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={e => { e.stopPropagation(); cancelarEdicao(); }}
                      disabled={salvandoEdicao}
                      className="p-1.5 md:p-1 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 disabled:opacity-40 transition-colors rounded"
                      title="Cancelar (Esc)"
                    >
                      <XMarkIcon className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-0.5 flex-shrink-0 ml-1">
                    <button
                      onClick={e => iniciarEdicao(e, agrupamento)}
                      className={`p-1.5 md:p-1 text-zinc-300 dark:text-zinc-600 hover:text-blue-600 dark:hover:text-blue-400 transition-colors rounded focus:opacity-100
                        ${selecionado ? 'md:opacity-100' : 'md:opacity-0 md:group-hover:opacity-100'}`}
                      title="Renomear grupo"
                    >
                      <PencilSquareIcon className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        onExcluirAgrupamento(agrupamento);
                      }}
                      className={`p-1.5 md:p-1 text-zinc-300 dark:text-zinc-600 hover:text-rose-500 dark:hover:text-rose-400 transition-colors rounded focus:opacity-100
                        ${selecionado ? 'md:opacity-100' : 'md:opacity-0 md:group-hover:opacity-100'}`}
                      title="Excluir grupo"
                    >
                      <TrashIcon className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
